import { useState } from "react";
import type { ChangeEvent } from "react";
import { baseUrl } from "../api";
import buttonBackground from "../assets/button_background_blue.webp";

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

interface ApiResponse {
  success: boolean;
  error?: string;
}

export default function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    const fieldName = name as keyof FormErrors;
    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: undefined,
      }));
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    try {
      const response = await fetch(baseUrl + "/accounts/api/contact/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }),
      });

      const result = (await response.json()) as ApiResponse;

      if (result.success) {
        // Handle success
        // alert("Message sent successfully!");
      } else {
        // Handle error
        //alert(result.error);
      }
      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch {
      //      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = (): void => {
    setIsSubmitted(false);
    setFormData({
      name: "",
      email: "",
      subject: "",
      message: "",
    });
    setErrors({});
  };

  if (isSubmitted) {
    return (
      <div className="center_me" style={{ marginTop: 40 }}>
        <div style={{ textAlign: "center", maxWidth: 500 }}>
          <div
            style={{
              width: 64,
              height: 64,
              backgroundColor: "#4CAF50",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <svg
              style={{ width: 32, height: 32, color: "white" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2
            className="font_family_base"
            style={{ fontSize: 24, fontWeight: "bold", marginBottom: 10 }}
          >
            Thank You!
          </h2>
          <p className="font_family_base" style={{ marginBottom: 30 }}>
            Your message has been sent successfully. We&apos;ll get back to you
            soon!
          </p>
          <button onClick={resetForm} className="my-form-button-wide">
            Send Another Message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="center_me">
      <div style={{ maxWidth: 500, width: "100%", padding: "0 20px" }}>
        <div style={{ marginBottom: 20, marginTop: 20 }}>
          <div className="center_me" style={{ marginBottom: 20 }}>
            <h2
              className="font_family_base header_text"
              style={{
                fontSize: 28,
                fontWeight: "bold",
                margin: 0,
              }}
            >
              Contact Us
            </h2>
          </div>
          <label
            htmlFor="name"
            className="field-name font_family_base field_name_color"
          >
            Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="input text_black font_family_base"
            placeholder="Your full name"
            style={{
              borderColor: errors.name ? "#fa5252" : undefined,
            }}
          />
          {errors.name && (
            <p
              className="font_family_base error_text_color"
              style={{
                marginTop: 5,
                fontSize: 14,
              }}
            >
              {errors.name}
            </p>
          )}
        </div>

        <div style={{ marginBottom: 20 }}>
          <label
            htmlFor="email"
            className="field-name font_family_base field_name_color"
          >
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="input text_black font_family_base"
            placeholder="your.email@example.com"
            style={{
              borderColor: errors.email ? "#fa5252" : undefined,
            }}
          />
          {errors.email && (
            <p
              className="font_family_base error_text_color"
              style={{
                marginTop: 5,
                fontSize: 14,
              }}
            >
              {errors.email}
            </p>
          )}
        </div>

        <div style={{ marginBottom: 20 }}>
          <label
            htmlFor="subject"
            className="field-name font_family_base field_name_color"
          >
            Subject *
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className="input text_black font_family_base"
            placeholder="What's this about?"
            style={{
              borderColor: errors.subject ? "#fa5252" : undefined,
            }}
          />
          {errors.subject && (
            <p
              className="font_family_base error_text_color"
              style={{
                marginTop: 5,
                fontSize: 14,
              }}
            >
              {errors.subject}
            </p>
          )}
        </div>

        <div style={{ marginBottom: 20 }}>
          <label
            htmlFor="message"
            className="field-name font_family_base field_name_color"
          >
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={5}
            className="input text_black font_family_base"
            placeholder="Tell us what's on your mind..."
            style={{
              borderColor: errors.message ? "#fa5252" : undefined,
              resize: "vertical",
              minHeight: 120,
            }}
          />
          {errors.message && (
            <p
              className="font_family_base error_text_color"
              style={{
                marginTop: 5,
                fontSize: 14,
              }}
            >
              {errors.message}
            </p>
          )}
        </div>

        <div className="center_me" style={{ marginTop: 30 }}>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="my-form-button-wide button-base button-3d font_family_base"
            style={{
              marginTop: "5px",
              marginBottom: "5px",
              backgroundImage: `url(${buttonBackground})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              color: "white",
              fontWeight: 700,
            }}
            // className="my-form-button-wide font_family_base"
            // style={{
            //   backgroundImage: `url(${buttonBackground})`,
            //   opacity: isSubmitting ? 0.6 : 1,
            //   cursor: isSubmitting ? "not-allowed" : "pointer",
            //   color: "white",
            //   backgroundPositionX: "center",
            //   backgroundPositionY: "center",
            // }}
          >
            {isSubmitting ? "Sending..." : "Send Message"}
          </button>
        </div>
      </div>
    </div>
  );
}
